import { useEffect, useState } from 'react'
import { useUserService } from '@lib/hooks'
import { useRemoveLocalStorageItem } from '@lib/utils/local-storage'
import {
  useCountSubmitFromLocalStorage,
  useExpirationTimestampFromLocalStorage,
  useHasPromptsRemaining,
} from './local-storage-state'
import { UrlBasedUsageTracker } from '@lib/utils/tool-usage'

export const useShouldTriggerWall = (usageTracker?: UrlBasedUsageTracker): boolean => {
  const { isAnonymous, isPremium } = useUserService().currentUser
  const hasPromptsRemaining = useHasPromptsRemaining(isAnonymous, isPremium)
  const [expirationLocalStorageKey, expirationValue] = useExpirationTimestampFromLocalStorage(isAnonymous)
  const [countLocalStorageKey] = useCountSubmitFromLocalStorage(isAnonymous)
  const removeLocalStorageItem = useRemoveLocalStorageItem()
  const canUse = usageTracker?.checkToolUsageState().canUse

  const [shouldTriggerWall, setShouldTriggerWall] = useState<boolean>(false)

  useEffect(() => {
    // CAPI prompt cap may have different value from local storage value due to incognito, clearing browser state, changing accounts
    if (!hasPromptsRemaining) {
      setShouldTriggerWall(true)
      return
    }

    if (usageTracker) {
      setShouldTriggerWall(!canUse)
      return
    }

    const dateNow = Date.now()
    if (dateNow > expirationValue) {
      removeLocalStorageItem(expirationLocalStorageKey)
      removeLocalStorageItem(countLocalStorageKey)
      setShouldTriggerWall(false)
      return
    }

    setShouldTriggerWall(!hasPromptsRemaining)
  }, [
    hasPromptsRemaining,
    expirationValue,
    expirationLocalStorageKey,
    countLocalStorageKey,
    removeLocalStorageItem,
    usageTracker,
    canUse,
  ])

  return shouldTriggerWall
}
